import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { studentPromotionAPI } from '../../services/api';
import Swal from 'sweetalert2';
import './ProcessTahun.css';

const ProcessTahun = () => {
  const { t } = useTranslation(['bulkPromotion', 'common']);
  const { tahun } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const { schoolCode, schoolName, sourceYear, targetYear, tahunName } = location.state || {};

  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [availableClasses, setAvailableClasses] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [bulkAction, setBulkAction] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (schoolCode && tahun) {
      loadData();
    }
  }, [schoolCode, tahun]);

  const loadData = async () => {
    try {
      setLoading(true);

      console.log('Loading students for Tahun:', {
        tahun,
        schoolCode,
        sourceYear,
        targetYear
      });

      // Load students
      const studentsResponse = await studentPromotionAPI.getStudents(
        tahun,
        schoolCode,
        sourceYear,
        targetYear
      );

      console.log('Students response:', studentsResponse);

      if (studentsResponse.success) {
        setStudents(studentsResponse.data.students || []);
        setAvailableClasses(studentsResponse.data.availableClasses || []);
      } else {
        Swal.fire({
          icon: 'error',
          title: t('common:error'),
          text: studentsResponse.message || t('bulkPromotion:errors.loadStudents')
        });
      }
    } catch (err) {
      console.error('Error loading students:', err);
      Swal.fire({
        icon: 'error',
        title: t('common:error'),
        text: err.response?.data?.message || t('bulkPromotion:errors.loadStudents')
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedStudents.size === filteredStudents.length) {
      setSelectedStudents(new Set());
    } else {
      setSelectedStudents(new Set(filteredStudents.map(s => s.studentId)));
    }
  };

  const handleSelectStudent = (studentId) => {
    const newSelected = new Set(selectedStudents);
    if (newSelected.has(studentId)) {
      newSelected.delete(studentId);
    } else {
      newSelected.add(studentId);
    }
    setSelectedStudents(newSelected);
  };

  const handleStudentActionChange = (studentId, action, targetTahun, targetClass) => {
    setStudents(prevStudents =>
      prevStudents.map(student => {
        if (student.studentId === studentId) {
          return {
            ...student,
            currentAction: action,
            currentTargetTahun: targetTahun,
            currentTargetClass: targetClass,
            isProcessed: false // Mark as modified
          };
        }
        return student;
      })
    );
  };

  const handleBulkActionApply = () => {
    if (!bulkAction || selectedStudents.size === 0) {
      return;
    }

    // Apply action to all selected students
    setStudents(prevStudents =>
      prevStudents.map(student => {
        if (selectedStudents.has(student.studentId)) {
          let targetTahun = null;
          let targetClass = null;

          // Determine target Tahun and Class based on action
          if (bulkAction === 'PROMOTE') {
            targetTahun = student.suggestedTahun || null;
            // Auto-fill class with suggested class if available
            targetClass = student.suggestedClass || null;
          } else if (bulkAction === 'REPEAT') {
            targetTahun = student.currentTahun;
            // Auto-fill class with suggested class if available
            targetClass = student.suggestedClass || null;
          }

          return {
            ...student,
            currentAction: bulkAction,
            currentTargetTahun: targetTahun,
            currentTargetClass: targetClass,
            isProcessed: false
          };
        }
        return student;
      })
    );

    // Clear bulk action selection
    setBulkAction('');
  };


  const handleSaveAndProcess = async () => {
    // Validate that all PROMOTE/REPEAT students (not already processed) have target class
    const studentsToProcess = students.filter(s =>
      !s.isProcessed && (s.currentAction === 'PROMOTE' || s.currentAction === 'REPEAT')
    );

    const missingClass = studentsToProcess.filter(s => !s.currentTargetClass);

    if (missingClass.length > 0) {
      await Swal.fire({
        icon: 'warning',
        title: t('common:warning'),
        html: t('bulkPromotion:warnings.missingClass', { count: missingClass.length })
      });
      return;
    }

    // Build promotions array (only modified students - not already processed)
    const promotions = students
      .filter(s => s.currentAction && !s.isProcessed)
      .map(s => ({
        studentId: s.studentId,
        action: s.currentAction,
        targetTahun: s.currentTargetTahun,
        targetClass: s.currentTargetClass
      }));

    if (promotions.length === 0) {
      await Swal.fire({
        icon: 'info',
        title: t('common:info'),
        text: t('bulkPromotion:warnings.noChanges')
      });
      return;
    }

    // Confirm before processing
    const result = await Swal.fire({
      title: t('common:confirmAction'),
      html: `
        <div style="text-align: left;">
          <p><strong>${t('bulkPromotion:summary.title')}:</strong></p>
          <ul>
            <li>${promotions.filter(p => p.action === 'PROMOTE').length} ${t('bulkPromotion:status.promoted')}</li>
            <li>${promotions.filter(p => p.action === 'REPEAT').length} ${t('bulkPromotion:status.repeated')}</li>
            <li>${promotions.filter(p => p.action === 'GRADUATE').length} ${t('bulkPromotion:status.graduated')}</li>
            <li>${promotions.filter(p => p.action === 'TRANSFER_OUT').length} ${t('bulkPromotion:status.transferredOut')}</li>
          </ul>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: t('bulkPromotion:buttons.saveProcess'),
      cancelButtonText: t('bulkPromotion:buttons.cancel')
    });

    if (!result.isConfirmed) {
      return;
    }

    // Process promotions
    try {
      setSaving(true);

      const request = {
        schoolCode,
        sourceYear,
        targetYear,
        sourceTahun: tahun,
        promotions
      };

      console.log('Processing promotions:', request);

      const response = await studentPromotionAPI.processPromotions(request);

      console.log('Process response:', response);

      if (response.success) {
        const data = response.data;

        await Swal.fire({
          icon: 'success',
          title: t('bulkPromotion:success.title'),
          html: `
            <div style="text-align: left;">
              <p>${t('bulkPromotion:success.processed', { count: data.processedCount })}</p>
              <ul>
                <li>${t('bulkPromotion:success.promoted', { count: data.promotedCount })}</li>
                <li>${t('bulkPromotion:success.repeated', { count: data.repeatedCount })}</li>
                <li>${t('bulkPromotion:success.graduated', { count: data.graduatedCount })}</li>
                <li>${t('bulkPromotion:success.transferredOut', { count: data.transferredOutCount })}</li>
              </ul>
              ${data.errors.length > 0 ? `<p style="color: red;">${t('bulkPromotion:errors.someErrors', { count: data.errors.length })}</p>` : ''}
            </div>
          `
        });

        // Reload data
        await loadData();

        // Clear selections
        setSelectedStudents(new Set());
      } else {
        await Swal.fire({
          icon: 'error',
          title: t('common:error'),
          text: response.message || t('bulkPromotion:errors.processPromotions')
        });
      }
    } catch (err) {
      console.error('Error processing promotions:', err);
      await Swal.fire({
        icon: 'error',
        title: t('common:error'),
        text: err.response?.data?.message || t('bulkPromotion:errors.processPromotions')
      });
    } finally {
      setSaving(false);
    }
  };

  // Filter students by search term
  const filteredStudents = students.filter(student =>
    student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.icLast4.includes(searchTerm) ||
    student.currentClass.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate summary counts
  const summary = {
    total: students.length,
    promote: students.filter(s => s.currentAction === 'PROMOTE').length,
    repeat: students.filter(s => s.currentAction === 'REPEAT').length,
    graduate: students.filter(s => s.currentAction === 'GRADUATE').length,
    transferOut: students.filter(s => s.currentAction === 'TRANSFER_OUT').length,
    pending: students.filter(s => !s.currentAction).length
  };

  if (loading) {
    return (
      <div className="process-tahun-container">
        <div className="loading-spinner">
          <i className="fas fa-spinner fa-spin"></i>
          <p>{t('common:loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="process-tahun-container">
      {/* Header */}
      <div className="page-header">
        <button className="btn-back" onClick={() => navigate('/bulk-promotion')}>
          <i className="fas fa-arrow-left"></i> {t('bulkPromotion:backToOverview')}
        </button>
        <h1>
          <i className="fas fa-layer-group"></i>{' '}
          {t('bulkPromotion:processGrade', { tahun: tahunName || `Tahun ${tahun}` })}
        </h1>
      </div>

      {/* Info bar */}
      <div className="info-bar">
        <div className="info-item">
          <strong>{t('common:school')}:</strong> {schoolCode} - {schoolName}
        </div>
        <div className="info-item">
          <strong>{t('bulkPromotion:sourceYear')}:</strong> {sourceYear} → {targetYear}
        </div>
        <div className="info-item">
          <strong>{t('bulkPromotion:progress', { processed: students.filter(s => s.isProcessed).length, total: students.length })}</strong>
        </div>
      </div>

      {/* Controls */}
      <div className="controls-bar">
        {/* Search */}
        <div className="search-box">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder={t('common:search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Bulk actions */}
        <div className="bulk-actions">
          <button
            className="btn btn-select-all"
            onClick={handleSelectAll}
          >
            {selectedStudents.size === filteredStudents.length ? (
              <>
                <i className="fas fa-square"></i> {t('bulkPromotion:buttons.deselectAll')}
              </>
            ) : (
              <>
                <i className="fas fa-check-square"></i> {t('bulkPromotion:buttons.selectAll')} ({filteredStudents.length})
              </>
            )}
          </button>

          <select
            className="form-control"
            value={bulkAction}
            onChange={(e) => setBulkAction(e.target.value)}
            disabled={selectedStudents.size === 0}
          >
            <option value="">{t('bulkPromotion:actions.bulkActions')}</option>
            <option value="PROMOTE">{t('bulkPromotion:actions.promote')}</option>
            <option value="REPEAT">{t('bulkPromotion:actions.repeat')}</option>
            <option value="GRADUATE">{t('bulkPromotion:actions.graduate')}</option>
          </select>

          <button
            className="btn btn-apply"
            onClick={handleBulkActionApply}
            disabled={!bulkAction || selectedStudents.size === 0}
          >
            {t('bulkPromotion:buttons.applySelected')}
          </button>
        </div>
      </div>

      {/* Students table */}
      <div className="students-table-container">
        <table className="students-table">
          <thead>
            <tr>
              <th className="col-select">
                <input
                  type="checkbox"
                  checked={selectedStudents.size === filteredStudents.length && filteredStudents.length > 0}
                  onChange={handleSelectAll}
                />
              </th>
              <th className="col-no">{t('bulkPromotion:columns.no')}</th>
              <th className="col-name">{t('bulkPromotion:columns.name')}</th>
              <th className="col-ic">{t('bulkPromotion:columns.icLast4')}</th>
              <th className="col-class">{t('bulkPromotion:columns.currentClass')}</th>
              <th className="col-target-tahun">{t('bulkPromotion:columns.targetTahun')}</th>
              <th className="col-target-class">{t('bulkPromotion:columns.targetClass')}</th>
              <th className="col-action">{t('bulkPromotion:columns.action')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredStudents.length > 0 ? (
              filteredStudents.map((student, index) => (
                <tr
                  key={student.studentId}
                  className={`${student.isProcessed ? 'processed' : ''} ${student.isTransferred ? 'transferred' : ''}`}
                >
                  <td className="col-select">
                    <input
                      type="checkbox"
                      checked={selectedStudents.has(student.studentId)}
                      onChange={() => handleSelectStudent(student.studentId)}
                      disabled={student.isTransferred}
                    />
                  </td>
                  <td className="col-no">{index + 1}</td>
                  <td className="col-name">
                    {student.name}
                    {student.isTransferred && (
                      <span className="transferred-badge" title={`Pindah ke ${student.transferredToSchool || 'sekolah lain'}`}>
                        <i className="fas fa-exchange-alt"></i>
                      </span>
                    )}
                  </td>
                  <td className="col-ic">{student.icLast4}</td>
                  <td className="col-class">{student.currentClass}</td>
                  <td className="col-target-tahun">
                    {student.isTransferred ? (
                      <span className="transferred-label">{student.transferredToSchool || 'PINDAH'}</span>
                    ) : (
                      <>
                        {student.currentAction === 'PROMOTE' && student.currentTargetTahun}
                        {student.currentAction === 'REPEAT' && `${student.currentTahun} (RPT)`}
                        {student.currentAction === 'GRADUATE' && 'GRAD'}
                        {student.currentAction === 'TRANSFER_OUT' && 'OUT'}
                        {!student.currentAction && '-'}
                      </>
                    )}
                  </td>
                  <td className="col-target-class">
                    {student.isTransferred ? (
                      <span className="transferred-label">-</span>
                    ) : (student.currentAction === 'PROMOTE' || student.currentAction === 'REPEAT') ? (
                      <select
                        className="form-control form-control-sm"
                        value={student.currentTargetClass || ''}
                        onChange={(e) => handleStudentActionChange(
                          student.studentId,
                          student.currentAction,
                          student.currentTargetTahun,
                          e.target.value
                        )}
                      >
                        <option value="">-</option>
                        {availableClasses.map(cls => (
                          <option key={cls.value} value={cls.value}>
                            {cls.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="col-action">
                    {student.isTransferred ? (
                      <span className="status-badge status-transferred">
                        {t('bulkPromotion:status.transferredOut')}
                      </span>
                    ) : (
                      <select
                        className="form-control form-control-sm"
                        value={student.currentAction || ''}
                        onChange={(e) => {
                          const action = e.target.value;
                          let targetTahun = null;
                          let targetClass = null;

                          if (action === 'PROMOTE') {
                            targetTahun = student.suggestedTahun;
                            // Auto-fill class with suggested class if available
                            targetClass = student.suggestedClass || null;
                          } else if (action === 'REPEAT') {
                            targetTahun = student.currentTahun;
                            // Auto-fill class with suggested class if available
                            targetClass = student.suggestedClass || null;
                          }

                          handleStudentActionChange(
                            student.studentId,
                            action,
                            targetTahun,
                            targetClass
                          );
                        }}
                      >
                        <option value="">-</option>
                        <option value="PROMOTE">{t('bulkPromotion:actions.promote')}</option>
                        <option value="REPEAT">{t('bulkPromotion:actions.repeat')}</option>
                        <option value="GRADUATE">{t('bulkPromotion:actions.graduate')}</option>
                        <option value="TRANSFER_OUT">{t('bulkPromotion:actions.transferOut')}</option>
                      </select>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="no-data">
                  {t('bulkPromotion:warnings.noStudents')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Summary and actions */}
      <div className="summary-bar">
        <div className="summary-info">
          <h4>{t('bulkPromotion:summary.title')}</h4>
          <div className="summary-grid">
            <div className="summary-item promote">
              <i className="fas fa-arrow-up"></i> {summary.promote} {t('bulkPromotion:status.promoted')}
            </div>
            <div className="summary-item repeat">
              <i className="fas fa-redo"></i> {summary.repeat} {t('bulkPromotion:status.repeated')}
            </div>
            <div className="summary-item graduate">
              <i className="fas fa-graduation-cap"></i> {summary.graduate} {t('bulkPromotion:status.graduated')}
            </div>
            <div className="summary-item transfer">
              <i className="fas fa-exchange-alt"></i> {summary.transferOut} {t('bulkPromotion:status.transferredOut')}
            </div>
            <div className="summary-item pending">
              <i className="fas fa-clock"></i> {summary.pending} {t('bulkPromotion:summary.pending', { count: summary.pending })}
            </div>
          </div>
        </div>

        <div className="action-buttons">
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/bulk-promotion')}
          >
            {t('bulkPromotion:buttons.cancel')}
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSaveAndProcess}
            disabled={saving || summary.promote + summary.repeat + summary.graduate + summary.transferOut === 0}
          >
            {saving ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> {t('common:processing')}
              </>
            ) : (
              <>
                <i className="fas fa-save"></i> {t('bulkPromotion:buttons.saveProcess')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProcessTahun;
